import { gql, useQuery } from "@apollo/client";
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Link,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { useFlags } from "@resource/client-ffs";
import { strings } from "@resource/common";
import {
  ActionEnum,
  EditActionEnum,
  GreenhouseJobStage,
  InterviewEditsDispatchType,
} from "components/legacy/BulkEdit/EditInterviewKits/types";
import Loading from "components/legacy/Loading/Loading";
import FeatureFlags from "generated/FeatureFlags";
import {
  GreenhouseJobData,
  GreenhouseJobDataVariables,
} from "generated/schemaTypes";
import _ from "lodash";
import React, { useMemo } from "react";
import { ResourceCustomTheme } from "styles/config";

const useGroupedStageCellStyles = makeStyles((_theme: ResourceCustomTheme) => ({
  root: {},
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  linkCell: {
    textAlign: "right",
  },
  link: {
    textDecoration: "underline",
  },
}));

type GroupedStageCellProps = {
  stages: GreenhouseJobStage[];
  name: string;
  onSelectInterview(selection: {
    numStages: number;
    ids: string[];
    name: string;
  }): void;
  onHideInterview(selection: {
    numStages: number;
    ids: string[];
    name: string;
  }): void;
};

function GroupedStageCell({
  name,
  stages,
  onSelectInterview,
  onHideInterview,
}: GroupedStageCellProps) {
  const {
    [FeatureFlags.EDIT_INTERVIEWS_BULK_ACTION]: editInterviewsEnabled,
    [FeatureFlags.HIDE_INTERVIEWS_BULK_ACTION]: hideInterviewsEnabled,
  } = useFlags();
  const classes = useGroupedStageCellStyles();
  const interviewMapping = useMemo(
    () => _(stages).flatMap("rawJson.interviews").groupBy("name").value(),
    [stages]
  );

  return (
    <TableRow key={name}>
      <TableCell>
        <Typography>{name}</Typography>
        {stages.length > 1 && (
          <Typography variant="subtitle2">
            Present in {stages.length} stages
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Table>
          <TableBody>
            {_.map(interviewMapping, (interviews, interviewName) => (
              <TableRow key={interviewName}>
                <TableCell>
                  <Typography variant="body1">{interviewName}</Typography>
                </TableCell>
                <TableCell className={classes.linkCell}>
                  {editInterviewsEnabled && (
                    <div>
                      <Button
                        color="primary"
                        className={classes.link}
                        component={Link}
                        onClick={() =>
                          onSelectInterview({
                            numStages: stages.length,
                            ids: _.map(interviews, "id"),
                            name: interviewName,
                          })
                        }
                      >
                        Edit {strings.pluralize("interview", interviews.length)}
                      </Button>
                    </div>
                  )}
                  {hideInterviewsEnabled && (
                    <div>
                      <Button
                        color="primary"
                        className={classes.link}
                        component={Link}
                        onClick={() =>
                          onHideInterview({
                            numStages: stages.length,
                            ids: _.map(interviews, "id"),
                            name: interviewName,
                          })
                        }
                      >
                        Hide {strings.pluralize("interview", interviews.length)}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCell>
    </TableRow>
  );
}

const GREENHOUSE_JOB_DATA = gql`
  query GreenhouseJobData($where: JobRoleWhereInput) {
    jobRoles(where: $where) {
      id
      atsJob {
        id
        atsJobStages(orderBy: [{ position: asc }]) {
          rawJson
          id
          name
        }
      }
    }
  }
`;

const useStyles = makeStyles((_theme: ResourceCustomTheme) => ({
  root: {},
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
}));

type SelectInterviewsProps = {
  dispatch: React.Dispatch<InterviewEditsDispatchType>;
  jobRoleIds: string[];
};

function SelectInterviews({ dispatch, jobRoleIds }: SelectInterviewsProps) {
  const classes = useStyles();
  const onSelectInterview = ({
    numStages,
    ids,
    name,
  }: {
    numStages: number;
    ids: string[];
    name: string;
  }) => {
    dispatch({
      type: ActionEnum.SetInterviews,
      interviewSelection: {
        numStages,
        ids,
        name,
      },
    });
    dispatch({
      type: ActionEnum.SetEditAction,
      editAction: EditActionEnum.Show,
    });
    dispatch({ type: ActionEnum.Next });
  };

  const onHideInterview = ({
    numStages,
    ids,
    name,
  }: {
    numStages: number;
    ids: string[];
    name: string;
  }) => {
    dispatch({
      type: ActionEnum.SetInterviews,
      interviewSelection: {
        numStages,
        ids,
        name,
      },
    });
    dispatch({
      type: ActionEnum.SetEditAction,
      editAction: EditActionEnum.Hide,
    });
    dispatch({ type: ActionEnum.Next });
  };

  const { data, loading } = useQuery<
    GreenhouseJobData,
    GreenhouseJobDataVariables
  >(GREENHOUSE_JOB_DATA, {
    variables: {
      where: {
        isArchived: false,
        id: {
          _in: jobRoleIds,
        },
      },
    },
  });

  const stageMapping = _(data?.jobRoles)
    .flatMap("atsJob.atsJobStages")
    .filter((s) => !!s.rawJson.interviews.length)
    .groupBy("name")
    .value() as unknown as Record<string, GreenhouseJobStage[]>;

  return loading ? (
    <Loading />
  ) : (
    <>
      <DialogContent dividers>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Grouped Stages</TableCell>
              <TableCell>Grouped Interviews</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {_.map(stageMapping, (stages, name) => (
              <GroupedStageCell
                key={name}
                stages={stages}
                name={name}
                onSelectInterview={onSelectInterview}
                onHideInterview={onHideInterview}
              />
            ))}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions>
        <Grid container>
          <Grid item xs={12} className={classes.actions}>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => {
                dispatch({
                  type: ActionEnum.Cancel,
                });
              }}
              style={{ marginLeft: 10 }}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}

export default SelectInterviews;
